import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutGrid, ClipboardList, Users, Package, HardHat, Bot, Search, History, Settings, LogOut, Menu, CreditCard, UserCog, Shield } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Logo } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";

const MGR = ["owner", "service_writer"];
const LINKS = [
  { to: "/app", label: "Shop board", icon: LayoutGrid, end: true, id: "dashboard" },
  { to: "/app/work-orders", label: "Work orders", icon: ClipboardList, id: "work-orders" },
  { to: "/app/customers", label: "Customers & units", icon: Users, id: "customers", roles: MGR },
  { to: "/app/inventory", label: "Inventory", icon: Package, id: "inventory", roles: MGR },
  { to: "/app/technicians", label: "Technicians", icon: HardHat, id: "technicians", roles: MGR },
  { to: "/app/assistant", label: "AI assistant", icon: Bot, id: "assistant" },
  { to: "/app/parts", label: "AI parts search", icon: Search, id: "parts" },
  { to: "/app/history", label: "Search history", icon: History, id: "history" },
  { to: "/app/staff", label: "Staff logins", icon: UserCog, id: "staff", roles: ["owner"] },
  { to: "/app/settings", label: "Settings", icon: Settings, id: "settings", roles: ["owner"] },
  { to: "/app/billing", label: "Billing", icon: CreditCard, id: "billing", always: true },
  { to: "/app/admin", label: "Admin panel", icon: Shield, id: "admin", admin: true },
];

const visible = (l, u) => {
  if (l.admin) return u?.role === "admin";
  if (!u?.access?.has_access) return l.always;
  return !l.roles || l.roles.includes(u.shop_role);
};

const NavItems = ({ onNav }) => {
  const { user } = useAuth();
  return (
  <nav className="flex flex-col gap-1">
    {LINKS.filter((l) => visible(l, user)).map((l) => (
      <NavLink key={l.to} to={l.to} end={l.end} onClick={onNav} data-testid={`nav-${l.id}`}
        className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${isActive ? "bg-primary text-white" : "text-muted-foreground hover:text-white hover:bg-secondary"}`}>
        <l.icon className="h-4 w-4" /> {l.label}
      </NavLink>
    ))}
  </nav>
  );
};

const UserBox = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = { owner: "Owner", service_writer: "Service writer", technician: "Technician" }[user?.shop_role];
  return (
    <div className="border-t border-border pt-4 flex items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="font-mono text-xs text-muted-foreground truncate" data-testid="header-user-email">{user?.email}</p>
        <p className="font-mono text-[10px] uppercase tracking-widest text-primary" data-testid="header-user-role">{user?.role === "admin" ? "Platform admin" : role}</p>
      </div>
      <button onClick={async () => { await logout(); navigate("/"); }} className="p-2 text-muted-foreground hover:text-white" data-testid="logout-btn">
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
};

export default function AppShell({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen lg:pl-64">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col gap-8 border-r border-border bg-[#0B0F17] p-5 z-30">
        <Logo />
        <div className="flex-1 overflow-y-auto"><NavItems /></div>
        <UserBox />
      </aside>
      <header className="lg:hidden sticky top-0 z-30 h-14 flex items-center justify-between px-4 border-b border-border bg-[#0B0F17]/90 backdrop-blur-xl">
        <Logo />
        <button onClick={() => setOpen(true)} className="p-2" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="bg-[#0B0F17] border-border w-72 flex flex-col gap-8">
          <Logo />
          <div className="flex-1"><NavItems onNav={() => setOpen(false)} /></div>
          <UserBox />
        </SheetContent>
      </Sheet>
      <main className="grid-bg min-h-screen">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-10 pb-32">{children}</div>
      </main>
    </div>
  );
}
